import React, { useCallback } from 'react'
import parse from 'date-fns/parse'

import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import Collapse from '@mui/material/Collapse'

import ShowMoreContent from '../components/ShowMoreContent'
import {
  AGENCY_MAILING_ADDRESS_MEDIA_TYPES,
  DAYFORCE_AGENCY_PREFERENCES_ATTACH_ORDER,
  DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE,
  DAYFORCE_AGENCY_PREFERENCES_DOCUMENTS_SORT_BY,
  DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS,
  DAYFORCE_AGENCY_PREFERENCES_MAIL_METHODS,
  DAYFORCE_AGENCY_PREFERENCES_SORT_ORDER,
} from 'utils/constants'
import getFrequencyDisplay from 'helpers/datetime/getFrequencyDisplay'
import isEmptyObject from 'helpers/node/isEmptyObject'
import useConfiguration from 'hooks/useConfiguration'
import { Divider } from '@mui/material'

const documentTypeColumns = {
  [DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.DAILY_OR_MONTHLY_CHECKS_AND_COUPONS]:
    [
      'primary_mailing_address',
      'overnight_mailing_address',
      'alternate_mailing_address',
      'zero_coupons',
      'applied_fors',
      'reports',
      'cd',
      'agency_package',
      'due_date',
      'special_handling',
      'additional_requirements',
    ],
  [DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS]: [
    'due_date',
    'agency_package',
    'additional_requirements',
  ],
}

function DayforceAgencyPreferencesExpandedList({
  expanded,
  rowData,
  statusTitle = '',
}) {
  const { instanceConfigurations } = useConfiguration()
  const agencyMailingAddressMediaTypes =
    instanceConfigurations?.[AGENCY_MAILING_ADDRESS_MEDIA_TYPES] || []

  const getMediaTypeDisplay = (mediaType) => {
    return agencyMailingAddressMediaTypes.find(
      (m) => m.toLowerCase() === mediaType.toLowerCase()
    )
  }

  const getStartDate = (date) => {
    if (!date) return new Date()

    try {
      if (date instanceof Date) return date

      return parse(date, 'yyyy-MM-dd', new Date())
    } catch (err) {
      return new Date()
    }
  }

  const getDataDisplay = useCallback(
    (row, col) => {
      let value = row[col] ?? {}

      if (col === 'zero_coupons') {
        return (
          <Box>
            <Box>• Send: {value.send ? 'Yes' : 'No'}</Box>
          </Box>
        )
      }

      if (col === 'applied_fors') {
        return (
          <Box>
            <Box>• Send: {value.send ? 'Yes' : 'No'}</Box>
            {Boolean(value.mail_with_checks && value.send) && (
              <Box>• Mail with Checks</Box>
            )}
            {Boolean(value.mailing_address && value.send) && (
              <Box sx={{ textTransform: 'capitalize' }}>
                • Mail To: {value.mailing_address.split('_').join(' ')}
              </Box>
            )}
          </Box>
        )
      }

      if (col === 'cd') {
        return (
          <Box>
            <Box>• Generate CD: {value.generate_cd ? 'Yes' : 'No'}</Box>
            {Boolean(value.mail_with_checks && value.generate_cd) && (
              <Box>• Mail with Checks</Box>
            )}
            {Boolean(value.mailing_address && value.generate_cd) && (
              <Box sx={{ textTransform: 'capitalize' }}>
                • Mail To: {value.mailing_address.split('_').join(' ')}
              </Box>
            )}
          </Box>
        )
      }

      if (col === 'special_handling') {
        if (!value.required) return <Box>• Requires Special Handling: No</Box>
        if (!value.instructions)
          return <Box>• Requires Special Handling: Yes</Box>

        return (
          <Box>
            <Box>• Requires Special Handling: Yes</Box>
            <Box
              component="pre"
              sx={{ whiteSpace: 'pre-wrap', p: 0, m: 0, pl: 2 }}
            >
              {value.instructions}
            </Box>
          </Box>
        )
      }

      if (col === 'reports') {
        return (
          <Box>
            <Box>
              • Generate Reports: {value.generate_reports ? 'Yes' : 'No'}
            </Box>
            {Boolean(value.mail_with_checks && value.generate_reports) && (
              <Box>• Mail with Checks</Box>
            )}
            {Boolean(value.mailing_address && value.generate_reports) && (
              <Box sx={{ textTransform: 'capitalize' }}>
                • Mail To: {value.mailing_address.split('_').join(' ')}
              </Box>
            )}
          </Box>
        )
      }

      if (col === 'agency_package') {
        if (
          rowData.document_type?.toLowerCase() ===
          DAYFORCE_AGENCY_PREFERENCES_DOCUMENT_TYPE.QUARTER_END_RETURNS.toLowerCase()
        ) {
          const renderSection = (sectionName, sectionData) => {
            if (!sectionData) return null

            const validIds = sectionData.valid_ids || {}
            const appliedFors = sectionData.applied_fors || {}

            const getValue = (key, data) => {
              return data[key] ? 'Yes' : 'No'
            }

            const getEfileMethod = (data) => {
              if (data.file_electronically && data.efile_method) {
                // Normalize the efile_method value for lookup
                // Try original value first, then normalized (underscore to space)
                const method = data.efile_method.toLowerCase()
                const normalizedMethod = method.replace(/_/g, ' ')
                const displayMethod =
                  DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS[method] ||
                  DAYFORCE_AGENCY_PREFERENCES_EFILE_METHODS[normalizedMethod] ||
                  data.efile_method
                return (
                  <Box sx={{ fontSize: 12, fontStyle: 'italic', mt: 0.5 }}>
                    ({displayMethod})
                  </Box>
                )
              }
              return null
            }

            const rows = []

            if (sectionName === 'return' || sectionName === 'wage_detail') {
              rows.push({
                label: 'File Electronically',
                validIds: getValue('file_electronically', validIds),
                appliedFors: getValue('file_electronically', appliedFors),
                validIdsEfile: getEfileMethod(validIds),
                appliedForsEfile: getEfileMethod(appliedFors),
              })
              rows.push({
                label: 'File Paper',
                validIds: getValue('file_paper', validIds),
                appliedFors: getValue('file_paper', appliedFors),
              })
              rows.push({
                label: 'File on CD',
                validIds: getValue('file_on_cd', validIds),
                appliedFors: getValue('file_on_cd', appliedFors),
              })
              rows.push({
                label: 'Include Zeros',
                validIds: getValue('include_zeros', validIds),
                appliedFors: getValue('include_zeros', appliedFors),
              })
              rows.push({
                label: 'Include Dollars',
                validIds: getValue('include_dollars', validIds),
                appliedFors: getValue('include_dollars', appliedFors),
              })
            } else if (sectionName === 'payment_types') {
              rows.push({
                label: 'ACH/EFT',
                validIds: getValue('eft_ach', validIds),
                appliedFors: getValue('eft_ach', appliedFors),
              })
              rows.push({
                label: 'Individual Check',
                validIds: getValue('individual_check', validIds),
                appliedFors: getValue('individual_check', appliedFors),
              })
              rows.push({
                label: 'Batch Check',
                validIds: getValue('batch_check', validIds),
                appliedFors: getValue('batch_check', appliedFors),
              })
              rows.push({
                label: 'Payment Voucher',
                validIds: getValue('payment_voucher', validIds),
                appliedFors: getValue('payment_voucher', appliedFors),
              })
            } else if (sectionName === 'other_requirements') {
              rows.push({
                label: 'Cover Letter',
                validIds: getValue('cover_letter', validIds),
                appliedFors: getValue('cover_letter', appliedFors),
              })
              rows.push({
                label: 'Other Documents',
                validIds: getValue('other_documents', validIds),
                appliedFors: getValue('other_documents', appliedFors),
              })
              rows.push({
                label: 'Client Listing Required',
                validIds: getValue('client_listing_required', validIds),
                appliedFors: getValue('client_listing_required', appliedFors),
              })
              rows.push({
                label: 'Out of Balance Accepted',
                validIds: getValue('out_of_balance_accepted', validIds),
                appliedFors: getValue('out_of_balance_accepted', appliedFors),
              })
            }

            return (
              <Box key={sectionName} sx={{ mb: 3 }}>
                <Typography fontSize={14} fontWeight="medium" sx={{ mb: 1 }}>
                  {sectionName
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')}
                </Typography>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 0.5,
                    overflow: 'hidden',
                  }}
                >
                  <Grid container>
                    <Grid
                      item
                      xs={4}
                      sx={{
                        p: 1,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'grey.50',
                        fontWeight: 500,
                      }}
                    >
                      <Typography fontSize={12} fontWeight={600}>
                        Option
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      sx={{
                        p: 1,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'grey.50',
                        textAlign: 'center',
                        fontWeight: 600,
                      }}
                    >
                      <Typography fontSize={12} fontWeight={600}>
                        Valid IDs
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      xs={4}
                      sx={{
                        p: 1,
                        backgroundColor: 'grey.50',
                        textAlign: 'center',
                        fontWeight: 600,
                      }}
                    >
                      <Typography fontSize={12} fontWeight={600}>
                        Applied Fors
                      </Typography>
                    </Grid>
                    {rows.map((row, index) => (
                      <React.Fragment key={index}>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            p: 1,
                            borderRight: '1px solid',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            backgroundColor:
                              index % 2 === 0 ? 'white' : 'grey.50',
                          }}
                        >
                          <Typography fontSize={12}>{row.label}</Typography>
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            p: 1,
                            borderRight: '1px solid',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            textAlign: 'center',
                            backgroundColor:
                              index % 2 === 0 ? 'white' : 'grey.50',
                          }}
                        >
                          <Typography fontSize={12}>{row.validIds}</Typography>
                          {row.validIdsEfile}
                        </Grid>
                        <Grid
                          item
                          xs={4}
                          sx={{
                            p: 1,
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            textAlign: 'center',
                            backgroundColor:
                              index % 2 === 0 ? 'white' : 'grey.50',
                          }}
                        >
                          <Typography fontSize={12}>
                            {row.appliedFors}
                          </Typography>
                          {row.appliedForsEfile}
                        </Grid>
                      </React.Fragment>
                    ))}
                  </Grid>
                </Box>
              </Box>
            )
          }

          return (
            <Grid container columnSpacing={6}>
              <Grid item xs={12}>
                <Divider sx={{ mt: 2, mb: 2 }} />
              </Grid>
              <Grid item xs={9}>
                <Box>
                  {value.return && renderSection('return', value.return)}
                  {value.wage_detail &&
                    renderSection('wage_detail', value.wage_detail)}
                  {value.payment_types &&
                    renderSection('payment_types', value.payment_types)}
                  {value.other_requirements &&
                    renderSection(
                      'other_requirements',
                      value.other_requirements
                    )}
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Typography fontSize={14} fontWeight="medium" sx={{ mb: 1 }}>
                  Mailing Information
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    flexWrap: 'wrap',
                  }}
                >
                  {Object.entries(
                    rowData.mailing_preferences?.agency_package
                      ?.mailing_information || {}
                  )
                    .filter(([_, addressValue]) => Boolean(addressValue))
                    .map(([key, addressValue]) => {
                      const mediaType =
                        getMediaTypeDisplay(addressValue.media_type) ||
                        addressValue.media_type ||
                        'N/A'
                      const mailMethod =
                        DAYFORCE_AGENCY_PREFERENCES_MAIL_METHODS[
                          addressValue.mail_method
                        ] ||
                        addressValue.mail_method ||
                        'N/A'
                      const attachOrder = addressValue.attach_order
                        ? DAYFORCE_AGENCY_PREFERENCES_ATTACH_ORDER[
                            addressValue.attach_order
                          ]
                        : 'N/A'
                      const sortBy = addressValue.sort_documents_sort_by
                        ? DAYFORCE_AGENCY_PREFERENCES_DOCUMENTS_SORT_BY[
                            addressValue.sort_documents_sort_by
                          ]
                        : 'N/A'

                      return (
                        <Box key={key} sx={{ minWidth: 250 }}>
                          <Box sx={{ mb: 1 }}>
                            <Typography fontSize={14} fontWeight="bold">
                              {mediaType?.toUpperCase?.()}
                            </Typography>
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            <Typography fontSize={12} component="span">
                              Mail Method:{' '}
                            </Typography>
                            <Typography
                              fontSize={12}
                              fontWeight="medium"
                              component="span"
                            >
                              {mailMethod}
                            </Typography>
                          </Box>
                          <Box sx={{ mb: 1 }}>
                            <Typography fontSize={12} component="span">
                              Attach/Order:{' '}
                            </Typography>
                            <Typography
                              fontSize={12}
                              fontWeight="medium"
                              component="span"
                            >
                              {attachOrder}
                            </Typography>
                          </Box>
                          {sortBy !== 'N/A' && (
                            <Box sx={{ mb: 1 }}>
                              <Typography fontSize={12} component="span">
                                Sort By:{' '}
                              </Typography>
                              <Typography
                                fontSize={12}
                                fontWeight="medium"
                                component="span"
                              >
                                {sortBy}
                              </Typography>
                            </Box>
                          )}
                          <Box sx={{ mt: 2 }}>
                            {addressValue.title && (
                              <Box fontSize={12}>{addressValue.title}</Box>
                            )}
                            {addressValue.department && (
                              <Box fontSize={12}>{addressValue.department}</Box>
                            )}
                            {addressValue.address_line_1 && (
                              <Box fontSize={12}>
                                {addressValue.address_line_1}
                              </Box>
                            )}
                            {addressValue.address_line_2 && (
                              <Box fontSize={12}>
                                {addressValue.address_line_2}
                              </Box>
                            )}
                            <Box fontSize={12}>
                              {addressValue.city || ''}
                              {addressValue.city && addressValue.state && ', '}
                              {addressValue.state || ''}{' '}
                              {addressValue.zip || ''}
                            </Box>
                            {addressValue.country && (
                              <Box fontSize={12}>{addressValue.country}</Box>
                            )}
                          </Box>
                        </Box>
                      )
                    })}

                  {Object.entries(
                    rowData.mailing_preferences?.agency_package
                      ?.mailing_information || {}
                  ).filter(([_, addressValue]) => Boolean(addressValue))
                    .length === 0 && (
                    <Box>
                      <Typography fontSize={12}>N/A</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          )
        } else {
          return (
            <Box>
              <Box>
                • Hold Checks to Due Date:{' '}
                {value.hold_checks_to_due_date ? 'Yes' : 'No'}
              </Box>
              <Box>
                • Sort Order:{' '}
                {DAYFORCE_AGENCY_PREFERENCES_SORT_ORDER[value.sort_order] ??
                  DAYFORCE_AGENCY_PREFERENCES_SORT_ORDER.clientid_dollar_coupon_check}
              </Box>
            </Box>
          )
        }
      }

      if (col === 'mailing_information') {
        return (
          <Box>
            <Divider sx={{ mt: 1, mb: 2 }} />
            <Box sx={{ display: 'flex', gap: 8 }}>
              {Object.entries(value)
                .filter(([_, addressValue]) => Boolean(addressValue))
                .map(([key, addressValue]) => ({
                  id: key,
                  ...addressValue,
                  media_type:
                    getMediaTypeDisplay(addressValue.media_type) ||
                    addressValue.media_type ||
                    'N/A',
                  mail_method:
                    DAYFORCE_AGENCY_PREFERENCES_MAIL_METHODS[
                      addressValue.mail_method
                    ],
                }))
                .map((row) => (
                  <Box key={row.id}>
                    <Box>
                      <Typography fontSize={14} fontWeight="bold">
                        {row.media_type?.toUpperCase?.()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Typography fontSize={14}>Mail Method:</Typography>
                      <Typography fontSize={14} fontWeight="medium">
                        {row.mail_method}
                      </Typography>
                    </Box>
                    <Box>{`${row.title || ''}`}</Box>
                    <Box>{`${row.department || ''}`}</Box>
                    <Box>{`${row.address_line_1 || ''}`}</Box>
                    {row.address_line_2 && (
                      <Box>{`${row.address_line_2 || ''}`}</Box>
                    )}
                    {`${row.city || ''}`}, {`${row.state || ''}`}{' '}
                    {`${row.zip || ''}`}
                  </Box>
                ))}
            </Box>
          </Box>
        )
      }

      if (!value || isEmptyObject(value)) return 'N/A'

      if (col === 'additional_requirements') {
        return (
          <Box>
            <Box component="pre" sx={{ whiteSpace: 'pre-wrap', p: 0, m: 0 }}>
              {value}
            </Box>
          </Box>
        )
      }

      if (col === 'due_date') {
        return (
          <Box>
            {getFrequencyDisplay(
              getStartDate(value.start_date),
              value.frequency
            )}
          </Box>
        )
      }

      if (
        col === 'primary_mailing_address' ||
        col === 'overnight_mailing_address' ||
        col === 'alternate_mailing_address'
      ) {
        return (
          <Box>
            <Box>{`${value.recipient_line_1 || ''}`}</Box>
            <Box>{`${value.recipient_line_2 || ''}`}</Box>
            <Box>{`${value.address1 || ''}`}</Box>
            {value.address2 && <Box>{`${value.address2 || ''}`}</Box>}
            {`${value.city || ''}`}, {`${value.state || ''}`}{' '}
            {`${value.zip || ''}`}
            <Box>{`${value.country || ''}`}</Box>
            {col === 'overnight_mailing_address' && row.always_ship_overnight && (
              <Typography
                sx={{ fontSize: 12, color: 'primary.main' }}
                fontWeight="bold"
              >
                ALWAYS SHIP OVERNIGHT
              </Typography>
            )}
          </Box>
        )
      }

      return `${row[col]}`
    },
    [rowData]
  )

  return (
    <Collapse
      in={expanded}
      sx={{
        textAlign: 'left',
        px: 11,
        py: expanded && 2,
        mt: 0,
        '& .MuiCollapse-entered': {
          mt: 4,
        },
      }}
    >
      {statusTitle && (
        <Typography fontWeight="bold" fontSize={14}>
          {statusTitle}
        </Typography>
      )}

      <ShowMoreContent
        rowData={rowData.mailing_preferences || {}}
        columns={(() => {
          // Define all possible columns
          const allColumns =
            documentTypeColumns[rowData.document_type?.toLowerCase()] || []

          // Sort to put priority columns first
          const sortedColumns = allColumns.sort((a, b) => {
            const aIndex = allColumns.indexOf(a)
            const bIndex = allColumns.indexOf(b)

            // Both are priority columns - sort by their order in priorityColumns
            if (aIndex !== -1 && bIndex !== -1) {
              return aIndex - bIndex
            }

            // Only a is priority - a comes first
            if (aIndex !== -1 && bIndex === -1) {
              return -1
            }

            // Only b is priority - b comes first
            if (aIndex === -1 && bIndex !== -1) {
              return 1
            }

            // Neither is priority - maintain original order
            return 0
          })

          return sortedColumns.map((key) => ({
            id: key,
            display: key.replace(/_/g, ' ').toUpperCase(),
          }))
        })()}
        open={true}
        getDataDisplay={getDataDisplay}
        visibleColumns={[]}
      />
    </Collapse>
  )
}

export default DayforceAgencyPreferencesExpandedList
